import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Clock, ArrowRight, ClipboardCheck, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceService } from '@/services/compliance-service';
import type { AssetCompliance } from '@/types/compliance';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface UpcomingInspection {
  id: number;
  assetId: string;
  assetName: string;
  assetCode: string;
  regimeName: string;
  nextInspectionDate: string;
  daysUntilDue: number;
  priority: 'High' | 'Medium' | 'Low';
  status: 'DueSoon' | 'Upcoming';
}

export function UpcomingInspectionsWidget() {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<UpcomingInspection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUpcomingInspections();
  }, []);

  const loadUpcomingInspections = async () => {
    try {
      setLoading(true);
      const compliances = await complianceService.getAssetCompliances();
      
      // Filter for upcoming inspections (next 30 days) and exclude disposed/sold assets
      const upcoming = compliances
        .filter((item: AssetCompliance) => {
          if (!item.nextInspectionDate) return false;
          if (item.assetStatus === 'Disposed' || item.assetStatus === 'Sold') return false;
          
          const daysUntil = Math.ceil(
            (new Date(item.nextInspectionDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
          );
          
          return daysUntil >= 0 && daysUntil <= 30;
        })
        .map((item: AssetCompliance) => {
          const daysUntil = Math.ceil(
            (new Date(item.nextInspectionDate!).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
          );
          
          return {
            id: item.id,
            assetId: item.assetId,
            assetName: item.assetName,
            assetCode: item.assetCode,
            regimeName: item.regimeName,
            nextInspectionDate: item.nextInspectionDate!,
            daysUntilDue: daysUntil,
            priority: daysUntil <= 7 ? 'High' : daysUntil <= 14 ? 'Medium' : 'Low',
            status: daysUntil <= 7 ? 'DueSoon' : 'Upcoming',
          } as UpcomingInspection;
        })
        .sort((a: UpcomingInspection, b: UpcomingInspection) => a.daysUntilDue - b.daysUntilDue)
        .slice(0, 5); // Show top 5

      setInspections(upcoming);
    } catch (error) {
      console.error('Failed to load upcoming inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'destructive';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const handleViewAll = () => {
    navigate('/compliance/assets');
  };

  const handleInspectionClick = (inspection: UpcomingInspection) => {
    navigate(`/assets/${inspection.assetId}`);
  };

  if (loading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Upcoming Inspections</CardTitle>
              <CardDescription>Due in the next 30 days</CardDescription>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (inspections.length === 0) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Upcoming Inspections</CardTitle>
              <CardDescription>Due in the next 30 days</CardDescription>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ClipboardCheck className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No inspections due in the next 30 days
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col')}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Upcoming Inspections</CardTitle>
            <CardDescription>Due in the next 30 days</CardDescription>
          </div>
          <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {inspections.map((inspection) => (
          <div
            key={inspection.id}
            className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
            onClick={() => handleInspectionClick(inspection)}
          >
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium truncate">
                  {inspection.assetName}
                </p>
                <Badge variant={getPriorityColor(inspection.priority)} className="text-xs shrink-0">
                  {inspection.priority}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {inspection.regimeName}
              </p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(inspection.nextInspectionDate)}
                </span>
                {inspection.status === 'DueSoon' && (
                  <span className="flex items-center gap-1 text-destructive font-medium">
                    <AlertCircle className="h-3 w-3" />
                    Due in {inspection.daysUntilDue} {inspection.daysUntilDue === 1 ? 'day' : 'days'}
                  </span>
                )}
                {inspection.status === 'Upcoming' && (
                  <span>
                    In {inspection.daysUntilDue} {inspection.daysUntilDue === 1 ? 'day' : 'days'}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAll}
          className="w-full mt-2"
        >
          View All Inspections
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
